<script setup>
import XlTooltip from './XlTooltip.vue';

const props = defineProps({
  // [{ id, label, count }] — count may be omitted where a tab has no total.
  tabs: { type: Array, required: true },
  modelValue: { type: [String, Number], required: true },
});

const emit = defineEmits(['update:modelValue']);
</script>

<template>
  <div class="xl-tabs" role="tablist">
    <button
      v-for="tab in props.tabs"
      :key="tab.id"
      type="button"
      role="tab"
      :aria-selected="tab.id === props.modelValue"
      class="xl-tabs__tab"
      :class="{ 'is-active': tab.id === props.modelValue }"
      @click="emit('update:modelValue', tab.id)"
    >
      <!--
        The tooltip wraps the label rather than the button: `control` keeps the
        wrapper out of the tab order, which the tab itself already occupies.
      -->
      <XlTooltip v-if="tab.tooltip" :text="tab.tooltip" placement="bottom" control underline>{{ tab.label }}</XlTooltip>
      <template v-else>{{ tab.label }}</template>{{ ' ' }}<span
        v-if="tab.count !== undefined && tab.count !== null"
        class="xl-tabs__count"
      >{{ tab.count }}</span>
    </button>
  </div>
</template>

<style scoped>
.xl-tabs, .xl-tabs * { box-sizing: border-box; }

.xl-tabs {
  display: flex;
  gap: 28px;
  margin: 0 0 22px;
  padding: 0;
  /* An inset line, not border-bottom: overflow-x: auto clips at the padding
     edge, so the active indicator has to sit inside the box to overlap it. */
  box-shadow: inset 0 -1px 0 var(--xl-color-border);
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}

.xl-tabs__tab {
  position: relative;
  padding: 0 0 13px;
  border: 0;
  background: none;
  font-family: var(--xl-font-family);
  font-size: var(--xl-font-size-md);
  font-weight: 600;
  color: var(--xl-color-secondary-text);
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.14s;
}

.xl-tabs__tab::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: transparent;
  transition: background 0.14s;
}

.xl-tabs__tab:hover { color: var(--xl-color-primary-text); }
.xl-tabs__tab:hover::after { background: var(--xl-color-primary-fixed-dim); }
.xl-tabs__tab.is-active { color: var(--xl-color-primary); }
.xl-tabs__tab.is-active::after { background: var(--xl-color-primary); }

.xl-tabs__tab:focus-visible {
  outline: 2px solid var(--xl-color-primary);
  outline-offset: 3px;
}

.xl-tabs__count {
  display: inline-block;
  margin-left: 7px;
  padding: 1px 8px;
  border-radius: var(--xl-radius-pill);
  background: var(--xl-color-border-subtle, var(--xl-color-border));
  color: var(--xl-color-outline);
  font-size: var(--xl-type-label-small-size);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  /* Two digits' room whatever it holds, so the place held while the counts
     load and a count of 1 or 48 are one width, and the tabs after it do not
     nudge sideways when the numbers land. */
  box-sizing: border-box;
  min-width: calc(2ch + 16px);
  text-align: center;
}

.xl-tabs__tab.is-active .xl-tabs__count {
  background: var(--xl-color-primary-container);
  color: var(--xl-color-primary);
}
</style>
